import React from 'react'
import { Divider, Expander, ExpanderHeader, ExpanderContent, ResponsiveTableRow } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { toLocaleString } from '../../../util/DateUtil'
import { PersonLink, UserLink } from '../../app/Links'
import { RawData } from '../../common/RawData'
import { enumToCamelCase } from '../../../util/StringUtil'

const SORTING_ARRAY = [
  'CONCLUSION_TYPE',
  'RELATIONSHIP_TYPE',
  'FACT_TYPE',
  'USER_DEFINED_DATA_TYPE',
  'USER_DEFINED_VALUE',
  'RELATIONSHIP_ID',
  'DATE',
  'PLACE',
  'SPOUSE_1_ID',
  'SPOUSE_2_ID',
  'CHILD_ID',
  'PARENT_1_ID',
  'PARENT_2_ID',
  'PERSON_ID',
  'CONTRIBUTOR_ID',
  'SUBMITTER_ID',
  'CHANGE_MESSAGE',
]

// Sort by the order in the SORTED_ARRAY
const sortRows = (rows) => {
  rows.sort((a, b) => {
    return SORTING_ARRAY.indexOf(a.key) - SORTING_ARRAY.indexOf(b.key)
  })
}

export default function RelationshipChangesRender({ changes, idsMetadata }) {
  const { t } = useTranslation()

  const ChangeHeader = ({ title, timestamp, contributorId, contributorName, submitterId, submitterName }) => (
    <ExpanderHeader>
      <div
        style={{
          userSelect: 'text',
        }}
      >
        <span style={{ fontWeight: 'bold' }}>
          {`${title}`}
          <span style={{ fontWeight: 'normal' }}>{` - ${timestamp} ${t('by.label.lowercase', 'by')} `}</span>
          <UserLink userId={contributorId} userName={contributorName} /> (
          <UserLink userId={submitterId} userName={submitterName} />)
        </span>
      </div>
    </ExpanderHeader>
  )

  const ChangeTuple = ({ label, detail }) => (
    <div>
      <span>{label}:&nbsp;</span>
      <span style={{ fontWeight: 'bold' }}>{detail}</span>
    </div>
  )

  const AttributionDetails = ({ eventOp, agent, changeMessage, changeId }) => {
    return (
      <>
        <ChangeTuple label={t('eventOp', 'Event Op')} detail={eventOp} keyId={`attrib1${changeId}${eventOp}`} />
        <ChangeTuple label={t('changes.agent', 'Agent')} detail={agent} keyId={`attrib2${changeId}${agent}`} />
        <ChangeTuple
          label={t('changes.changeId', 'Change Id')}
          detail={changeId}
          keyId={`attrib3${changeId}${changeId}`}
        />
        <ChangeTuple
          label={t('changeMessage', 'Change Message')}
          detail={changeMessage}
          keyId={`attrib4${changeId}${changeMessage}`}
        />
      </>
    )
  }

  const ChangeValues = ({ rows, displayOld, changeId }) => {
    const keyAddend = displayOld ? 'old' : 'new'
    return (
      <>
        {rows &&
          rows.map((row) => {
            let value = displayOld ? row.oldValue : row.newValue
            if (value) {
              switch (row.key) {
                case 'SPOUSE_1_ID':
                case 'SPOUSE_2_ID':
                case 'CHILD_ID':
                case 'PARENT_1_ID':
                case 'PARENT_2_ID':
                case 'PERSON_ID': {
                  const friendlyName = idsMetadata[value]?.friendlyName
                  return (
                    <ChangeTuple
                      label={t(`changes.field.label.${row.key}`, row.key)}
                      detail={<PersonLink personId={value} personName={friendlyName} showId />}
                      key={changeId + keyAddend + row.key + value}
                    />
                  )
                }
                case 'CONTRIBUTOR_ID':
                case 'SUBMITTER_ID': {
                  const friendlyName = idsMetadata[value]?.friendlyName
                  return (
                    <ChangeTuple
                      label={t(`changes.field.label.${row.key}`, row.key)}
                      detail={<UserLink userId={value} userName={friendlyName} showId />}
                      key={changeId + keyAddend + row.key + value}
                    />
                  )
                }
                default:
                  if (row.key === 'SCOPE') {
                    value = t(`changes.fieldType.scope.${value}`, value)
                  } else if (row.key === 'CONCLUSION_TYPE') {
                    value = t(`changes.fieldType.conclusionType.${value}`, value)
                  } else if (row.key === 'DISMISSED_SUGGESTION_TYPE') {
                    value = t(`changes.fieldType.dismissedSubmissionType.${value}`, value)
                  } else if (row.key === 'TYPE') {
                    value = t(`changes.fieldType.type.${value}`, value)
                  } else if (row.key === 'RELATIONSHIP_TYPE') {
                    value = t(`changes.fieldType.relationshipType.${value}`, value)
                  }

                  return (
                    <ChangeTuple
                      label={t(`changes.field.label.${row.key}`, row.key)}
                      detail={value}
                      key={changeId + keyAddend + row.key + value}
                    />
                  )
              }
            }
            return null
          })}
      </>
    )
  }

  return changes.map((change) => {
    const { metadata, rawPersonChange, rows } = change
    const { attribution, changeId, title } = metadata
    const { changeMessage, contributorId, modifiedDateMilliseconds, submitterId } = attribution
    const timestamp = toLocaleString(modifiedDateMilliseconds, true)
    const contributorName = idsMetadata[contributorId]?.friendlyName
    const submitterName = idsMetadata[submitterId]?.friendlyName
    const eventOp = metadata.op ? t(`person.changes.${metadata.op}`, enumToCamelCase(metadata.op)) : metadata.title
    const agent = idsMetadata[attribution.agentAppId]?.friendlyName
    sortRows(rows)

    return (
      <React.Fragment key={metadata.changeId}>
        <Expander initiallyOpen>
          <ChangeHeader
            title={title}
            timestamp={timestamp}
            contributorId={contributorId}
            contributorName={contributorName}
            submitterId={submitterId}
            submitterName={submitterName}
          />
          <ExpanderContent>
            <DmcResponsiveTable
              id="relationshipChangesTable"
              totalColumnCount={3}
              atSizeColumns={{
                xl: {
                  labels: [t('attribution.label', 'Attribution'), t('old', 'Old'), t('new', 'New')],
                },
                md: {
                  labels: [t('attribution.label', 'Attribution'), `${t('old', 'Old')}  & ${t('new', 'New')}`],
                  combinationRules: [['column1'], ['column2', 'column3']],
                },
                xs: {
                  labels: [
                    `${t('attribution.label', 'Attribution')} &
                          ${t('old', 'Old')}  & ${t('new', 'New')}`,
                  ],
                  combinationRules: [['column1', 'column2', 'column3']],
                },
                default: {
                  labels: [t('attribution.label', 'Attribution'), t('old', 'Old'), t('new', 'New')],
                },
              }}
            >
              <ResponsiveTableRow
                key={changeId}
                column1={
                  <AttributionDetails
                    eventOp={eventOp}
                    agent={agent}
                    changeMessage={changeMessage}
                    changeId={changeId}
                  />
                }
                column2={<ChangeValues rows={rows} displayOld changeId={changeId} />}
                column3={<ChangeValues rows={rows} changeId={changeId} />}
              />
            </DmcResponsiveTable>
          </ExpanderContent>
        </Expander>
        <RawData state={rawPersonChange} />
        <Divider />
        <br />
      </React.Fragment>
    )
  })
}
